import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import ssPropTypes from '../../../modules/ssPropTypes';
import './Cell.scss';
import { BodyText, Touchable } from '..';
import { useIsMobile } from '../../../hooks';

/**
 * Renders an individual date, supporting assignment of arbitrary classes. "Stock" supplied classes
 * are `today`, `disabled`, and `date-cell`.
 * @param {Object} param0 Component props
 * @param {Object} [param0.selected=false] True if the cell is selected
 * @param {Boolean} [param0.disabled=false] True if the cell is disabled
 * @param {String} [param0.className] A string of classes
 * @param {moment} param0.value The date to display
 * @param {Function} [param.onClick] Called when the cell is clicked
 * @example
 * import DateCell from 'components/ui/date-picker/DateCell';
 *
 * const MyComponent = () => {
 *   return (
 *     <DateCell
 *       selected={true}
 *       className={'highlighted'}
 *       disabled={false}
 *       value={moment()}
 *       onClick={handleDateClick}
 *     />
 *   );
 * }
 */
const DateCell = ({
  selected,
  disabled,
  empty,
  value,
  onClick,
  startRange,
  endRange,
  selectedColor,
  selectedTextColor,
  selectToday,
}) => {
  const isMobile = useIsMobile();
  const today = value.isSame(moment(), 'day');
  let color = colors.metal;
  let backgroundColor = colors.whiteWash;
  let borderColor = colors.whiteWash;
  let todayTestIDSuffix = '';

  if (today && selectToday) {
    color = colors.whiteWash;
    backgroundColor = colors.slate;
    borderColor = colors.slate;
    todayTestIDSuffix = '-today';
  }

  if (selected) {
    color = selectedTextColor;
    backgroundColor = selectedColor;
    borderColor = selectedColor;
  } else if (disabled) {
    color = colors.silver;
  }

  if (empty) {
    backgroundColor = colors.whiteWash;
    borderColor = colors.whiteWash;
  }

  if (startRange || endRange) {
    color = colors.whiteWash;
    backgroundColor = colors.punch;
    borderColor = colors.punch;
  }

  return (
    <Touchable
      onClick={onClick}
      testID={`date-picker-cell-${value.format('MM-DD')}${todayTestIDSuffix}`}
    >
      <Box
        color={backgroundColor}
        borderWidth={1}
        borderStyle="solid"
        borderColor={borderColor}
        minHeight={isMobile ? 36 : 40}
        minWidth={isMobile ? 36 : 40}
        justifyContent="center"
      >
        <BodyText textAlign="center" color={color}>
          {empty ? '' : value.format('D')}
        </BodyText>
      </Box>
    </Touchable>
  );
};

DateCell.propTypes = {
  /* anything that is a valid argument for classnames */
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  selectToday: PropTypes.bool,
  empty: PropTypes.bool,
  value: ssPropTypes.moment.isRequired,
  startRange: PropTypes.bool,
  endRange: PropTypes.bool,
  selectedColor: PropTypes.string,
  selectedTextColor: PropTypes.string,
};

DateCell.defaultProps = {
  selected: false,
  selectToday: true,
  onClick: () => {},
  disabled: false,
  empty: false,
  startRange: false,
  endRange: false,
  selectedColor: colors.blackInBlack,
  selectedTextColor: colors.whiteWash,
};

export default DateCell;
